import { formatCurrency } from '@sidecar-utilities/textTransformers'

export interface CareEstimate {
  savings: number
  billOverBenefitAmount: number
  estimatedMemberResponsibility: number
  netEstimatedMemberResponsibility?: number
  earnedBenefit: number
  netEarnedBenefit?: number
  appliedToDeductible: number
  providerPrice: number
  // LIGHTS-4265: Coinsurance fields added by backend
  appliedCoinsurance?: number // Amount applied to coinsurance
  appliedMoop?: number // Sum of appliedToDeductible + appliedCoinsurance
  coinsuranceEligible?: number // Coinsurance basis (for future use)
}

export interface BundlePricingResult {
  youllPay: number
  youllEarn: number
  pricing: {
    type: 'pay' | 'earn'
    amount: number
    sublabel?: string
  }
}

/** Calculates pricing for all plans. */
const calculatePricing = ({
  estimatedMemberResponsibility = 0,
  earnedBenefit = 0,
  appliedToDeductible = 0,
  appliedCoinsurance = 0,
  appliedMoop = 0,
}: {
  estimatedMemberResponsibility?: number
  earnedBenefit?: number
  appliedToDeductible?: number
  appliedCoinsurance?: number
  appliedMoop?: number
}): { type: 'pay' | 'earn'; amount: number; sublabel?: string } => {
  // if the user has both a cost and earnings, they are HSA with an unmet deductible
  if (estimatedMemberResponsibility > 0 && earnedBenefit > 0) {
    return {
      type: 'pay',
      amount: estimatedMemberResponsibility,
      sublabel: `Earn ${formatCurrency(earnedBenefit)} after deductible is met`,
    }
  }

  if (earnedBenefit > 0) {
    return { type: 'earn', amount: earnedBenefit }
  }

  if (estimatedMemberResponsibility > 0) {
    const hasDeductible = appliedToDeductible > 0
    const hasCoinsurance = appliedCoinsurance > 0

    // LIGHTS-4265: Handle deductible + coinsurance combined
    if (hasDeductible && hasCoinsurance) {
      const portionDueToBoth = Math.min(
        appliedMoop,
        estimatedMemberResponsibility,
      )

      return {
        type: 'pay',
        amount: estimatedMemberResponsibility,
        sublabel:
          estimatedMemberResponsibility === portionDueToBoth
            ? 'Due to deductible/coinsurance'
            : `${formatCurrency(portionDueToBoth)} due to deductible/coinsurance`,
      }
    }

    // LIGHTS-4265: Handle deductible only (existing logic preserved)
    if (hasDeductible) {
      const portionDueToDeductible = Math.min(
        appliedToDeductible,
        estimatedMemberResponsibility,
      )

      return {
        type: 'pay',
        amount: estimatedMemberResponsibility,
        sublabel:
          // if 100% of the responsibility is due to deductible, don't show the number twice
          estimatedMemberResponsibility === portionDueToDeductible
            ? 'Due to deductible'
            : `${formatCurrency(portionDueToDeductible)} due to deductible`,
      }
    }

    // LIGHTS-4265: Handle coinsurance only
    if (hasCoinsurance) {
      const portionDueToCoinsurance = Math.min(
        appliedCoinsurance,
        estimatedMemberResponsibility,
      )

      return {
        type: 'pay',
        amount: estimatedMemberResponsibility,
        sublabel:
          estimatedMemberResponsibility === portionDueToCoinsurance
            ? 'Due to coinsurance'
            : `${formatCurrency(portionDueToCoinsurance)} due to coinsurance`,
      }
    }

    // No specific reason (no deductible or coinsurance applies)
    return { type: 'pay', amount: estimatedMemberResponsibility }
  }

  // If we have a $0 amount with no other qualifying conditions, just return pay with no sublabel
  return { type: 'pay', amount: 0 }
}

/**
 * Calculates bundle pricing from a care estimate.
 *
 * Uses the simple, reliable approach from the Review Page that works correctly:
 *
 * - Uses API response values directly without complex fallback logic
 * - Trusts the care estimate data from the backend
 * - Provides consistent pricing across all components
 *
 * @param careEstimate - The care estimate data from the API
 * @param enableCoinsurance - Whether to include coinsurance in subtitle logic
 *   (default: true). When false, coinsurance fields are ignored and only
 *   deductible subtitles are shown. This allows feature flag gating of
 *   coinsurance UI elements.
 * @returns Object containing youllPay, youllEarn, and pricing format
 */
export function calculateBundlePricing(
  careEstimate: CareEstimate | null | undefined,
  enableCoinsurance: boolean = true,
): BundlePricingResult {
  // Handle null/undefined case
  if (!careEstimate) {
    return {
      youllPay: 0,
      youllEarn: 0,
      pricing: {
        type: 'pay',
        amount: 0,
      },
    }
  }

  // Use the same simple logic as Review Page (which works correctly)
  const estimatedMemberResponsibility =
    careEstimate.netEstimatedMemberResponsibility >= 0
      ? careEstimate.netEstimatedMemberResponsibility
      : careEstimate.estimatedMemberResponsibility || 0

  // !!Sensitive code!! Be careful when editing!
  // There's a bug in the BE when deductible applies and there is earnings.
  // Instead of netEarnedBenefit being appliedToDeductible - earnings,
  // they make it benefit amount - earned benefit,
  // which makes netEarnedBenefit bigger than earnedBenefit which should never happen.
  const earnedBenefit =
    careEstimate.netEarnedBenefit >= 0 &&
    careEstimate.netEarnedBenefit < careEstimate.earnedBenefit
      ? careEstimate.netEarnedBenefit
      : careEstimate.earnedBenefit || 0

  const appliedToDeductible = careEstimate.appliedToDeductible || 0

  // LIGHTS-4265: Extract coinsurance fields from care estimate
  // When enableCoinsurance is false, treat coinsurance fields as 0 (feature flag gating)
  const appliedCoinsurance = enableCoinsurance
    ? careEstimate.appliedCoinsurance || 0
    : 0
  const appliedMoop = enableCoinsurance ? careEstimate.appliedMoop || 0 : 0

  // Determine pricing type and amount based on plan type
  const pricing = calculatePricing({
    estimatedMemberResponsibility,
    earnedBenefit,
    appliedToDeductible,
    appliedCoinsurance,
    appliedMoop,
  })

  const result = {
    youllPay: estimatedMemberResponsibility,
    youllEarn: earnedBenefit,
    pricing: {
      type: pricing.type,
      amount: pricing.amount,
      sublabel: pricing.sublabel,
    },
  }

  return result
}
